import { Grid, Box, Stack } from "@mui/material";
import React from "react";
import { sportLogos, siteLogos } from "../utils/logos";
import { SelectInput, MenuOption } from "./selectOptions";
import { SitesResponse, SlateResponse, SportsResponse } from "./store/types";
import { useHistory } from "react-router-dom";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { setSite, setSlate, setSport, setType } from "./store/actions";
import { connect } from "react-redux";
import { filterSlates, getSupportedSports, SupportedSites } from "./helpers/SelectorHelpers";
import { useGetSlates } from "./queries/slateQueries";

interface OwnProps {
  readonly title: string;
  readonly app: string;
  readonly backgroundColor: string;
}

interface ReduxProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedType: string;
}

interface DispatchProps {
  readonly SetSelectedSport: (newValue: SportsResponse) => void;
  readonly SetSelectedSite: (newValue: SitesResponse) => void;
  readonly SetSelectedType: (newValue: string) => void;
  readonly SetSelectedSlate: (newValue: SlateResponse) => void;

}

type Props = OwnProps & ReduxProps & DispatchProps;


const NoSlatesView = (props: Props) => {
  const { selectedSport, selectedSite, selectedType, backgroundColor, app, SetSelectedSport, SetSelectedSite, SetSelectedType, SetSelectedSlate } = props;
  const supportedSports = getSupportedSports(app);
  const supportedSites = SupportedSites;
  const { data } = useGetSlates(app, false);
  const slates = data?.slates;
  const history = useHistory();
  const [search, setSearch] = useQueryParams({ site: StringParam, slate: NumberParam });
  const handleSportSelect = (sport: SportsResponse) => {
    SetSelectedSport(sport);
    const newSlate = filterSlates(slates || [], sport, selectedSite, selectedType)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId) }, 'push');
    }
    else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined }, 'push');
    }
    history.push(`${sport.sport}`);
  }

  const handleSiteSelect = (site: SitesResponse) => {
    SetSelectedSite(site);
    const newSlate = filterSlates(slates || [], selectedSport, site, selectedType)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId) }, 'push');
    }
    else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined }, 'push');
    }
    setSearch({ ...search, site: site.site });
  };

  const handleTypeSelect = (type: string) => {
    const newSlate = filterSlates(slates || [], selectedSport, selectedSite, type)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId) }, 'push');
    }
    else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined }, 'push');
    }
    SetSelectedType(type);

  }

  return (
    <Stack sx={{
      borderRadius: 3,
      color: 'white',
      display: "flex",
      // flexDirection: "column",
      // overflow: "auto",
      '&::-webkit-scrollbar': {
        width: '0.2em',
      },
      '&::-webkit-scrollbar-track': {
        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'slategrey',
      },
    }}>
      <Grid container style={{ padding: '7px 20px', backgroundColor: backgroundColor }}>
        <Grid item xs={12} container alignItems="center">
          <Grid xs={12} md={3} sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', md: 'flex-start' },
          }}>
            <Box>
              <SelectInput
                data={supportedSports?.map(x => ({ name: x.sport, logo: sportLogos[x.sport?.toUpperCase()]?.logo, invertBackground: sportLogos[x.sport?.toUpperCase()]?.invertBackground, value: x.sport, option: x, } as MenuOption<SportsResponse>)) ?? []}
                label="League"
                value={selectedSport?.sport || 'NFL'}
                onSelect={(selection: any) => handleSportSelect(selection as SportsResponse)}
              />
            </Box>
            <Box>
              <SelectInput
                data={supportedSites?.map(x => ({ name: siteLogos[x.site?.toLowerCase()]?.name || x.site, logo: siteLogos[x.site?.toLowerCase()]?.logo || '', value: x.site, option: x, } as MenuOption<SitesResponse>))}
                label="Site"
                value={selectedSite?.site || 'DK'}
                onSelect={(selection: any) => handleSiteSelect(selection as SitesResponse)}
              />
            </Box>
            <Box>
              <SelectInput
                data={[{ name: 'Classic', value: 'CLASSIC', option: 'CLASSIC' }, { name: 'Showdown', value: 'SHOWDOWN', option: 'SHOWDOWN' }]}
                label="Type"
                value={selectedType || 'CLASSIC'}
                onSelect={(selection: string) => handleTypeSelect(selection)}
              />
            </Box>
          </Grid>
          <Grid xs={12} md={8}>
            <Box display='flex' alignItems={'center'} justifyContent='center' fontSize={14} fontWeight={700} sx={{}}>
              {
                selectedSport?.sport === "MLB" && selectedType.toLowerCase() === 'showdown' ? (
                  <>
                    MLB Showdown slates are not supported at this time
                  </>
                ) : (
                  <>
                    No slate upcoming for {selectedSport?.sport}. The Pre-Contest Simulator can only be used within {selectedSport?.sport === "PGA" ? 48 : 12} hours of a slate's start time and once projections have been released for the slate.
                    Please select a different sport, or come back at a time closer to lock.
                  </>
                )
              }
            </Box>
          </Grid>
          {/* <Stack direction='row' alignItems='center' display='flex'>
          </Stack> */}
        </Grid>
      </Grid>
    </Stack>
  )
}

const mapStateToProps = (state: any) => {
  const {
    selectedSport,
    selectedSite,
    selectedType,

  } = state.common;

  return {
    selectedSport,
    selectedSite,
    selectedType,
  };
};

const mapDispatchToProps = {
  SetSelectedSport: setSport,
  SetSelectedSite: setSite,
  SetSelectedType: setType,
  SetSelectedSlate: setSlate,

};

export default connect(mapStateToProps, mapDispatchToProps)(NoSlatesView);